import React, { useCallback, useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import ConfirmarModal from "./ConfirmarModal";
import { FilaChamado, FilaMembro, FilaOrganizacao } from "../../../../interfaces/FilaOrganizacao";
import { TICKET_STATE_LABELS, TicketState } from "../../../../interfaces/Ticket";
import {
  assumirChamado,
  atribuirChamado,
  comentarChamado,
  encaminharParaDataValue,
  getChamadoDaFila,
  getMembrosDaFila,
  mensagemDaFila,
  moverChamado,
  mudarEstadoChamado,
} from "../../../../services/filaOrganizacaoApi";

interface Props {
  ticketId: number;
  filas: FilaOrganizacao[];
  onClose: () => void;
  onChanged: () => void;
}

const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const btnSec =
  "rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5";
const lblCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

/** Detalhe e ações de um chamado da fila da organização. */
const FilaChamadoModal: React.FC<Props> = ({ ticketId, filas, onClose, onChanged }) => {
  const [ticket, setTicket] = useState<FilaChamado | null>(null);
  const [membros, setMembros] = useState<FilaMembro[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [texto, setTexto] = useState("");
  const [nota, setNota] = useState(false);
  const [confirmarEncaminhar, setConfirmarEncaminhar] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErro(null);
    getChamadoDaFila(ticketId)
      .then((t) => {
        setTicket(t);
        if (t.queueId) getMembrosDaFila(t.queueId).then(setMembros).catch(() => setMembros([]));
      })
      .catch((e) => setErro(mensagemDaFila(e, "Não foi possível carregar o chamado. Feche e tente de novo.")))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(load, [load]);

  const executar = async (acao: () => Promise<void>, sucesso: string, falha: string) => {
    setOcupado(true);
    try {
      await acao();
      toast.success(sucesso);
      load();
      onChanged();
      return true;
    } catch (e) {
      toast.error(mensagemDaFila(e, falha));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const enviar = async () => {
    const body = texto.trim();
    if (!body) return;
    const ok = await executar(
      () => comentarChamado(ticketId, body, nota),
      nota ? "Nota interna registrada." : "Resposta enviada ao solicitante.",
      "Não foi possível enviar."
    );
    if (ok) setTexto("");
  };

  const encaminhar = async () => {
    const ok = await executar(
      () => encaminharParaDataValue(ticketId),
      "Chamado encaminhado para a DataValue.",
      "Não foi possível encaminhar."
    );
    if (ok) setConfirmarEncaminhar(false);
  };

  const podeAtender = !!ticket?.podeAtender;
  const filasDestino = filas.filter((f) => f.ativa && f.id !== ticket?.queueId);

  return (
    <Modal onClose={onClose}>
      <div
        className="w-full max-w-2xl rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            {ticket ? `${ticket.number} · ${ticket.subject}` : "Chamado"}
          </h3>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:text-gray-400 dark:hover:bg-navy-700"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {loading && !ticket ? (
          <div role="status" className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
        ) : erro || !ticket ? (
          <p role="alert" className="py-8 text-center text-sm text-red-600 dark:text-red-400">
            {erro ?? "Chamado não encontrado."}
          </p>
        ) : (
          <div className="mt-3 flex max-h-[75vh] flex-col gap-4 overflow-y-auto pr-1">
            <dl className="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
              <div><dt className="text-xs font-semibold">Estado</dt><dd>{TICKET_STATE_LABELS[ticket.state] ?? ticket.stateName}</dd></div>
              <div><dt className="text-xs font-semibold">Fila</dt><dd>{ticket.queueName ?? "—"}</dd></div>
              <div><dt className="text-xs font-semibold">Atendente</dt><dd>{ticket.atendenteNome ?? "Sem atendente"}</dd></div>
              <div><dt className="text-xs font-semibold">Solicitante</dt><dd>{ticket.requesterEmail ?? "—"}</dd></div>
              <div><dt className="text-xs font-semibold">Aberto em</dt><dd>{fmtDateTime(ticket.openedAt)}</dd></div>
              <div><dt className="text-xs font-semibold">Prazo de resolução</dt><dd>{fmtDateTime(ticket.slaResolutionDueAt)}</dd></div>
            </dl>
            {ticket.description && (
              <p className="whitespace-pre-wrap rounded-xl bg-gray-50 p-3 text-sm text-gray-800 dark:bg-navy-900 dark:text-gray-200">
                {ticket.description}
              </p>
            )}

            {!podeAtender && (
              <p className="rounded-xl border border-dv-blue-100 bg-dv-blue-50 px-4 py-3 text-sm text-dv-blue-700 dark:border-dv-blue-900/40 dark:bg-dv-blue-900/20 dark:text-dv-cyan-200">
                Este chamado foi encaminhado para a DataValue. Você continua acompanhando, mas o atendimento agora é de lá.
              </p>
            )}

            {podeAtender && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label htmlFor="fila-atribuir" className={lblCls}>Atribuir a</label>
                  <select
                    id="fila-atribuir"
                    className={selectCls}
                    disabled={ocupado}
                    value=""
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      if (id) executar(() => atribuirChamado(ticketId, id), "Chamado atribuído.", "Não foi possível atribuir.");
                    }}
                  >
                    <option value="">Escolha um membro…</option>
                    {membros.map((m) => <option key={m.pessoaId} value={m.pessoaId}>{m.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="fila-estado" className={lblCls}>Mudar estado</label>
                  <select
                    id="fila-estado"
                    className={selectCls}
                    disabled={ocupado || ticket.allowedStates.length === 0}
                    value=""
                    onChange={(e) => {
                      const s = Number(e.target.value) as TicketState;
                      if (s) executar(() => mudarEstadoChamado(ticketId, s), "Estado atualizado.", "Transição não permitida.");
                    }}
                  >
                    <option value="">Escolha o novo estado…</option>
                    {ticket.allowedStates.map((s) => <option key={s} value={s}>{TICKET_STATE_LABELS[s]}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="fila-mover" className={lblCls}>Mover para a fila</label>
                  <select
                    id="fila-mover"
                    className={selectCls}
                    disabled={ocupado || filasDestino.length === 0}
                    value=""
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      if (id) executar(() => moverChamado(ticketId, id), "Chamado movido de fila.", "Não foi possível mover.");
                    }}
                  >
                    <option value="">Escolha a fila…</option>
                    {filasDestino.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2 sm:col-span-3">
                  <button type="button" className={btnSec} disabled={ocupado} onClick={() => executar(() => assumirChamado(ticketId), "Você assumiu o chamado.", "Não foi possível assumir.")}>
                    Assumir
                  </button>
                  <button type="button" className={btnSec} disabled={ocupado} onClick={() => setConfirmarEncaminhar(true)}>
                    Encaminhar para a DataValue
                  </button>
                </div>
              </div>
            )}

            <section aria-label="Comentários" className="flex flex-col gap-2">
              <h4 className="text-sm font-bold text-navy-700 dark:text-white">Comentários</h4>
              {ticket.comments.length === 0 ? (
                <p className="text-sm text-gray-700 dark:text-gray-300">Nenhum comentário ainda.</p>
              ) : (
                ticket.comments.map((c) => (
                  <div
                    key={c.id}
                    className={`rounded-xl border px-3 py-2 text-sm ${
                      c.isInternal
                        ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100"
                        : "border-gray-100 bg-gray-50 text-gray-800 dark:border-white/5 dark:bg-navy-900 dark:text-gray-200"
                    }`}
                  >
                    <p className="text-xs font-semibold">
                      {c.authorName ?? "—"} · {fmtDateTime(c.dtCreated)}
                      {c.isInternal && (
                        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          Nota interna
                        </span>
                      )}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
                  </div>
                ))
              )}
            </section>

            {podeAtender && (
              <div className="flex flex-col gap-2">
                <label htmlFor="fila-comentario" className={lblCls}>
                  {nota ? "Nota interna" : "Resposta ao solicitante"}
                </label>
                <textarea id="fila-comentario" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} className={selectCls} />
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={nota} onChange={(e) => setNota(e.target.checked)} />
                  Nota interna (o solicitante não vê)
                </label>
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={ocupado || !texto.trim()}
                    onClick={enviar}
                    className="rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
                  >
                    {nota ? "Registrar nota" : "Enviar resposta"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {confirmarEncaminhar && (
        <ConfirmarModal
          titulo="Encaminhar para a DataValue?"
          texto="O chamado sai da sua fila e passa a ser atendido pela DataValue. O número e o histórico continuam, e você segue podendo acompanhar, mas não atende mais por aqui."
          confirmarLabel="Encaminhar"
          ocupado={ocupado}
          onConfirm={encaminhar}
          onClose={() => setConfirmarEncaminhar(false)}
        />
      )}
    </Modal>
  );
};

export default FilaChamadoModal;
